import type { PerpOrderPlacedProperties } from '@/ampli';
import type {
  ApiRequestError,
  OrderParameters,
  OrderResponse,
  OrderSuccessResponse,
} from '@nktkas/hyperliquid/api/exchange';
import { formatPrice, formatSize } from '@nktkas/hyperliquid/utils';

import { coinLabel } from './coins';
import {
  BUILDER_ADDRESS,
  BUILDER_FEE_TENTHS_BPS,
  PERPS_MARKET_SLIPPAGE,
  PERPS_TRIGGER_SLIPPAGE,
} from './constants';
import { formatUsdPrice } from './prices';
import type {
  PerpCloseOrderType,
  PerpFill,
  PerpMarket,
  PerpOpenOrder,
  PerpOrderType,
  PerpPosition,
} from './types';

export type PerpOrder = OrderParameters['orders'][number];
export type PerpOrderStatus = OrderSuccessResponse['response']['data']['statuses'][number];

/**
 * Asset id for actions: the universe index on the validator-operated dex (index 0),
 * `100000 + dexIndex * 10000 + index` on HIP-3 dexs.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/asset-ids
 */
export const perpAssetIndex = (dexIndex: number, indexInMeta: number) =>
  dexIndex === 0 ? indexInMeta : 100_000 + dexIndex * 10_000 + indexInMeta;

type PerpOrderDraftBase = {
  assetIndex: number;
  szDecimals: number;
  isBuy: boolean;
};

type PerpEntryOrderDraft = PerpOrderDraftBase & {
  /** Size in coin units. */
  size: number;
  reduceOnly?: boolean;
} & ({ orderType: 'market'; markPx: number } | { orderType: 'limit'; price: number });

/**
 * Close orders are always reduce-only. Size `'position'` closes whatever the position is when
 * the order triggers; it is encoded as size `0`, which Hyperliquid only accepts under the
 * `positionTpsl` grouping against an existing position.
 */
type PerpCloseOrderDraft = PerpOrderDraftBase & {
  size: number | 'position';
  triggerPx: number;
} & ({ orderType: 'takeProfit' | 'stopMarket' } | { orderType: 'stopLimit'; price: number });

export type PerpOrderDraft = PerpEntryOrderDraft | PerpCloseOrderDraft;

export const isCloseOrderType = (orderType: PerpOrderType): orderType is PerpCloseOrderType =>
  orderType === 'takeProfit' || orderType === 'stopMarket' || orderType === 'stopLimit';

/** Hyperliquid's trigger kind: a take profit is `tp`; stop market and stop limit are `sl`. */
export const tpslKind = (orderType: PerpCloseOrderType): 'tp' | 'sl' =>
  orderType === 'takeProfit' ? 'tp' : 'sl';

/** A take profit sits past the reference price in the position's favour, a stop loss against it. */
export const triggersAbove = (tpsl: 'tp' | 'sl', positionIsLong: boolean) =>
  (tpsl === 'tp') === positionIsLong;

/** Full-position TP/SL rest at size 0 and take the position's size when they trigger. */
export const isFullPositionTpsl = (order: PerpOpenOrder) =>
  order.isPositionTpsl && Number(order.sz) === 0;

const OPEN_ORDER_TRIGGERS: Partial<
  Record<PerpOpenOrder['orderType'], { tpsl: 'tp' | 'sl'; isMarket: boolean }>
> = {
  'Take Profit Market': { tpsl: 'tp', isMarket: true },
  'Take Profit Limit': { tpsl: 'tp', isMarket: false },
  'Stop Market': { tpsl: 'sl', isMarket: true },
  'Stop Limit': { tpsl: 'sl', isMarket: false },
};

/** Trigger kind and execution of a resting trigger order; undefined for every other order. */
export const openOrderTrigger = (orderType: PerpOpenOrder['orderType']) =>
  OPEN_ORDER_TRIGGERS[orderType];

/** Hyperliquid has no native market order: it is an IOC limit priced through the mark by a slippage guard. */
export const marketGuardPrice = (markPx: number, isBuy: boolean) =>
  markPx * (isBuy ? 1 + PERPS_MARKET_SLIPPAGE : 1 - PERPS_MARKET_SLIPPAGE);

/**
 * Worst fill for a market TP/SL once it triggers. Hyperliquid rejects a sell whose `p` is above
 * `triggerPx` (a buy's below it) as "Invalid TP/SL price", so the guard follows the trigger.
 */
export const marketTriggerGuardPrice = (triggerPx: number, isBuy: boolean, szDecimals: number) =>
  formatPrice(
    triggerPx * (isBuy ? 1 + PERPS_TRIGGER_SLIPPAGE : 1 - PERPS_TRIGGER_SLIPPAGE),
    szDecimals,
  );

export function buildPerpOrder(draft: PerpOrderDraft): PerpOrder {
  const { assetIndex: a, szDecimals, isBuy: b, size } = draft;
  const s = size === 'position' ? '0' : formatSize(size, szDecimals);

  switch (draft.orderType) {
    case 'market':
      return {
        a,
        b,
        p: formatPrice(marketGuardPrice(draft.markPx, b), szDecimals),
        s,
        r: draft.reduceOnly ?? false,
        t: { limit: { tif: 'FrontendMarket' } },
      };
    case 'limit':
      return {
        a,
        b,
        p: formatPrice(draft.price, szDecimals),
        s,
        r: draft.reduceOnly ?? false,
        t: { limit: { tif: 'Gtc' } },
      };
    case 'takeProfit':
    case 'stopMarket':
      return {
        a,
        b,
        p: marketTriggerGuardPrice(draft.triggerPx, b, szDecimals),
        s,
        r: true,
        t: {
          trigger: {
            isMarket: true,
            triggerPx: formatPrice(draft.triggerPx, szDecimals),
            tpsl: tpslKind(draft.orderType),
          },
        },
      };
    case 'stopLimit':
      // Rests as a GTC limit at `p` once triggered.
      return {
        a,
        b,
        p: formatPrice(draft.price, szDecimals),
        s,
        r: true,
        t: {
          trigger: {
            isMarket: false,
            triggerPx: formatPrice(draft.triggerPx, szDecimals),
            tpsl: tpslKind(draft.orderType),
          },
        },
      };
    default: {
      const exhaustive: never = draft;
      throw new Error(`Unknown order type ${String(exhaustive)}`);
    }
  }
}

/**
 * Size an open order will trade. Full-position TP/SL rest at size 0 and take the position's
 * size when they trigger, so they read as the position they close. `szi` already carries
 * the market's size precision; only its sign is stripped.
 */
export const openOrderSize = (order: PerpOpenOrder, position: PerpPosition | undefined) =>
  isFullPositionTpsl(order) && position?.coin === order.coin
    ? position.szi.replace('-', '')
    : order.sz;

/**
 * Human-readable outcome of a placed order, reversing {@link buildPerpOrder}'s
 * encoding so every order type (market, limit, TP/SL, position close) reads right.
 */
export function describePlacedOrder(
  order: PerpOrder,
  status: PerpOrderStatus,
  coin: string,
): { title: string; description: string } | undefined {
  const side = order.b ? 'Long' : 'Short';
  if ('trigger' in order.t) {
    const { tpsl, isMarket, triggerPx } = order.t.trigger;
    const subject = order.s === '0' ? `${coin} position` : `${order.s} ${coin}`;
    return {
      title: `${tpsl === 'tp' ? 'Take profit' : isMarket ? 'Stop market' : 'Stop limit'} set`,
      description: isMarket
        ? `Closes ${subject} at $${triggerPx}`
        : `Closes ${subject} at $${order.p} once $${triggerPx} trades`,
    };
  }
  // Fills are announced from the userFills feed (see describeFillEvent) so that orders
  // filled on placement and standing orders filled later share one toast.
  if (typeof status === 'object' && 'filled' in status) return undefined;
  return {
    title: order.t.limit.tif === 'Gtc' ? 'Limit order placed' : 'Order placed',
    description: `${side} ${order.s} ${coin} at $${order.p}`,
  };
}

/**
 * Rejection message per order of a bulk action the exchange refused, undefined where the order
 * went through. A request refused before validation (and every `modify` error) is rejected
 * whole, with one message.
 */
export function rejectionMessages(count: number, error: ApiRequestError): (string | undefined)[] {
  const { response } = error.response as {
    response?: string | { data?: { statuses?: OrderResponse['response']['data']['statuses'] } };
  };
  const statuses = typeof response === 'object' ? response.data?.statuses : undefined;
  if (!statuses) return Array.from({ length: count }, () => error.message);
  return statuses.map((status) =>
    typeof status === 'object' && 'error' in status ? status.error : undefined,
  );
}

/** Order fields shared by the `perp_order_placed` and `perp_order_cancelled` events. */
export type PerpOrderAnalytics = Omit<
  PerpOrderPlacedProperties,
  'wallet_address' | 'leverage' | 'leverage_type'
>;

/** Full-position TP/SL resize with the position, so they are told apart from fixed-size triggers. */
const triggerAnalyticsType = (
  tpsl: 'tp' | 'sl',
  isMarket: boolean,
  closesEntirePosition: boolean,
): PerpOrderAnalytics['perp_order_type'] => {
  if (tpsl === 'tp') return closesEntirePosition ? 'position_tp' : 'take_profit';
  if (closesEntirePosition) return 'position_sl';
  return isMarket ? 'stop' : 'stop_limit';
};

/** Analytics fields of an order as sent to the exchange, decoded like {@link describePlacedOrder}. */
export function wireOrderAnalytics(order: Omit<PerpOrder, 'a'>, coin: string): PerpOrderAnalytics {
  // Modifications resend the open order's size, which may read "0.0".
  const closesEntirePosition = Number(order.s) === 0;
  const common = {
    coin,
    direction: order.b ? 'long' : 'short',
    order_size: closesEntirePosition ? undefined : Number(order.s),
    reduce_only: order.r,
  } as const;
  if ('limit' in order.t) {
    // A market order is an IOC limit whose `p` is only a slippage guard.
    return order.t.limit.tif === 'FrontendMarket'
      ? { ...common, perp_order_type: 'market' }
      : { ...common, perp_order_type: 'limit', limit_price: Number(order.p) };
  }
  const { tpsl, isMarket, triggerPx } = order.t.trigger;
  return {
    ...common,
    perp_order_type: triggerAnalyticsType(tpsl, isMarket, closesEntirePosition),
    limit_price: isMarket ? undefined : Number(order.p),
    trigger_price: Number(triggerPx),
  };
}

/** Wire order type of a resting order; undefined for kinds Matcha never places. */
function openOrderWireType(order: PerpOpenOrder): PerpOrder['t'] | undefined {
  const trigger = openOrderTrigger(order.orderType);
  if (trigger) return { trigger: { ...trigger, triggerPx: order.triggerPx } };
  if (order.orderType === 'Market') return { limit: { tif: 'FrontendMarket' } };
  // Only Gtc and Alo limits rest; the two read the same.
  if (order.orderType === 'Limit') return { limit: { tif: 'Gtc' } };
  return undefined;
}

/**
 * Analytics fields of an open order, decoded through its wire form so an order reads the same
 * when placed and when cancelled. Undefined for kinds Matcha never places.
 */
export function openOrderAnalytics(order: PerpOpenOrder): PerpOrderAnalytics | undefined {
  const t = openOrderWireType(order);
  if (!t) return undefined;
  return wireOrderAnalytics(
    { b: order.side === 'B', p: order.limitPx, s: order.sz, r: order.reduceOnly, t },
    order.coin,
  );
}

/**
 * One toast per order in a live `userFills` event, aggregating that order's fills within the
 * event; a resting order that fills partially over several events toasts each time. Fills on
 * coins outside `markets` (spot, other dexs) are ignored.
 */
export function describeFillEvent(
  fills: PerpFill[],
  marketByCoin: Record<string, PerpMarket>,
): { title: string; description: string }[] {
  const byOrder = new Map<number, PerpFill[]>();
  fills.forEach((fill) => byOrder.set(fill.oid, [...(byOrder.get(fill.oid) ?? []), fill]));
  return [...byOrder.values()].flatMap((orderFills) => {
    const [{ coin, dir, liquidation }] = orderFills;
    const market = marketByCoin[coin];
    if (!market) return [];
    const size = orderFills.reduce((sum, fill) => sum + Number(fill.sz), 0);
    const notional = orderFills.reduce((sum, fill) => sum + Number(fill.sz) * Number(fill.px), 0);
    const title = liquidation
      ? 'Position liquidated'
      : dir.startsWith('Close')
        ? 'Position closed'
        : 'Order filled';
    return {
      title,
      description: `${dir} ${formatSize(size, market.szDecimals)} ${coinLabel(coin)} at ${formatUsdPrice(notional / size, market.szDecimals)}`,
    };
  });
}

/** Wraps orders with the Matcha builder fee when a builder address is configured. */
export function buildOrderRequest(
  orders: PerpOrder[],
  grouping: OrderParameters['grouping'] = 'na',
): OrderParameters {
  return {
    orders,
    grouping,
    ...(BUILDER_ADDRESS && { builder: { b: BUILDER_ADDRESS, f: BUILDER_FEE_TENTHS_BPS } }),
  };
}
